export function createRevisionSearchTermsEditorController({
  select,
  getState,
  setState,
  normalizeSearchTermsDraft,
  enrichKeywordsWithVolume,
  renderKeywordChips,
  getMarketDataKeywords,
  buildVolumeMap,
  extractKeywordText,
  extractSearchVolume,
  updateByteCounter,
  packKeywordsByBytes,
  isAllowedForSearchTerms,
  maxSearchBytes,
  onByteLimitExceeded,
  onStateChanged,
  documentRef = globalThis.document,
} = {}) {
  function ensureKeywordsWithVolume(searchTerms) {
    if (!searchTerms) return searchTerms;
    if (searchTerms.keywords?.length > 0) return normalizeSearchTermsDraft(searchTerms);
    const text = searchTerms.searchTermsEn || '';
    if (!text) return searchTerms;
    const keywords = enrichKeywordsWithVolume(text.split(','));
    return normalizeSearchTermsDraft({
      ...searchTerms,
      keywords,
      candidates: keywords.map((keyword) => keyword.keyword),
      selected: keywords.map((keyword) => keyword.keyword),
    });
  }

  function getKeywords() {
    const draft = getState('revisionSearchTermsDraft') ?? getState('listingDraftSearchTerms') ?? {};
    const normalized = normalizeSearchTermsDraft(draft);
    if (normalized.keywords?.length) return normalized.keywords.map((item) => ({ ...item }));
    return normalized.searchTermsEn
      ? enrichKeywordsWithVolume(normalized.searchTermsEn.split(','))
      : [];
  }

  function syncKeywords(keywords) {
    const allowed = (keywords || []).filter((keyword) => keyword && isAllowedForSearchTerms(keyword.keyword));
    const packed = packKeywordsByBytes(allowed.map((keyword) => keyword.keyword), maxSearchBytes);
    const packedSet = new Set(packed.packed.map((keyword) => keyword.toLowerCase()));
    const packedAllowed = allowed.filter((keyword) => packedSet.has(String(keyword.keyword || '').toLowerCase()));
    const searchTermsEn = packed.packed.join(', ');
    const previous = getState('revisionSearchTermsDraft') || getState('listingDraftSearchTerms') || {};
    setState('revisionSearchTermsDraft', normalizeSearchTermsDraft({
      ...previous,
      searchTermsEn,
      keywords: packedAllowed,
      candidates: packedAllowed.map((keyword) => keyword.keyword),
      selected: packedAllowed.map((keyword) => keyword.keyword),
    }));
    if (packed.dropped.length) onByteLimitExceeded(packed.dropped.length);
    onStateChanged();
  }

  function renderBubbles(keywords) {
    const container = select('#search-terms-bubbles');
    renderKeywordChips(container, keywords, {
      selectedSet: () => true,
      onToggle: (keyword, checked) => {
        if (checked) return;
        const lower = String(keyword || '').toLowerCase();
        syncKeywords(getKeywords().filter((item) => String(item.keyword || '').toLowerCase() !== lower));
        setup();
      },
    });
  }

  function renderSuggestions(currentKeywords) {
    const container = select('#search-terms-suggestions');
    if (!container) return;
    const currentSet = new Set(currentKeywords.map((keyword) => keyword.keyword.toLowerCase()));
    const library = getState('keywordLibrary');
    const marketDataKeywords = getMarketDataKeywords();
    const candidates = [];
    if (library?.keywords) {
      for (const keyword of library.keywords) {
        if (!currentSet.has(keyword.keyword.toLowerCase()) && isAllowedForSearchTerms(keyword.keyword)) {
          candidates.push({ keyword: keyword.keyword, searches30d: keyword.searches30d || 0 });
        }
      }
    }
    for (const keyword of marketDataKeywords) {
      const text = extractKeywordText(keyword);
      if (text && !currentSet.has(text.toLowerCase()) && isAllowedForSearchTerms(text)) {
        candidates.push({ keyword: text, searches30d: extractSearchVolume(keyword) });
      }
    }
    candidates.sort((left, right) => (right.searches30d || 0) - (left.searches30d || 0));
    const visibleCandidates = candidates.slice(0, 30);
    if (visibleCandidates.length === 0) {
      const empty = documentRef.createElement('span');
      empty.className = 'revision-keyword-empty';
      empty.textContent = '暂无推荐词';
      container.replaceChildren(empty);
      return;
    }
    renderKeywordChips(container, visibleCandidates, {
      selectedSet: new Set(),
      onToggle: (keyword, checked) => {
        if (!checked) return;
        const updated = getKeywords();
        const volume = buildVolumeMap().get(keyword.toLowerCase()) || 0;
        updated.push({ keyword, searches30d: volume });
        syncKeywords(updated);
        setup();
      },
    });
  }

  function setup() {
    const editor = select('#search-terms-editor');
    if (!editor) return;
    const searchTerms = getState('revisionSearchTermsDraft') || getState('listingDraftSearchTerms');
    if (!searchTerms) {
      editor.hidden = true;
      return;
    }
    editor.hidden = false;
    const keywords = searchTerms.keywords?.length
      ? searchTerms.keywords
      : (searchTerms.searchTermsEn ? enrichKeywordsWithVolume(searchTerms.searchTermsEn.split(',')) : []);
    renderBubbles(keywords);
    renderSuggestions(keywords);
    const counter = select('#search-terms-byte-count');
    updateByteCounter(counter, keywords.map((keyword) => keyword.keyword).join(', '), maxSearchBytes);
  }

  function addFromSuggestion(keyword) {
    const tokens = String(keyword || '').trim()
      .split(/[，,/、;；]/)
      .map((item) => item.trim())
      .filter((item) => item && item.length <= 80);
    if (!tokens.length) return [];
    const volumeMap = buildVolumeMap();
    const previous = getState('revisionSearchTermsDraft') || getState('listingDraftSearchTerms') || {};
    const previousKeywords = Array.isArray(previous.keywords) ? previous.keywords : [];
    const existingSet = new Set(previousKeywords.map((item) => String(item.keyword || '').toLowerCase()));
    const added = [];
    const next = [...previousKeywords];
    for (const token of tokens) {
      const lower = token.toLowerCase();
      if (existingSet.has(lower) || !isAllowedForSearchTerms(token)) continue;
      next.push({ keyword: token, searches30d: volumeMap.get(lower) || 0 });
      existingSet.add(lower);
      added.push(token);
    }
    if (!added.length) return [];
    syncKeywords(next);
    if (documentRef?.querySelector('#search-terms-bubbles')) setup();
    return added;
  }

  return { addFromSuggestion, ensureKeywordsWithVolume, setup };
}
